import React, { useState, ChangeEvent } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormInput from "../../questionnaire/components/FormInput";
import FormSelect from "../../questionnaire/components/FormSelect";
import AppButton from "../../questionnaire/components/AppButton";
import CheckinLayout from "../../questionnaire/components/CheckinLayout";
import { useAppointmentStepPath } from "../../questionnaire/routes/stepConfig";
import { useDemographics } from "../../questionnaire/contexts/DemographicsContext";
import { useWorkflow } from "../../questionnaire/contexts/WorkflowContext";
import { useLoader } from "../../questionnaire/contexts/LoaderContext";
import { usStates } from "../../questionnaire/questionnaire_constants";
import { Alert } from "../../components/shared/Alert";
import axiosInstance from "../../utils/axiosConfig";

const AddressAndIdentity: React.FC = () => {
  console.log("AddressAndIdentity component mounted");
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { demographics, loading, error, updateDemographics } = useDemographics();
  const { getNextStepInSequence, getPreviousStep, isAllStepsCompleted, error: workflowError, loading: workflowLoading, refreshWorkflow } = useWorkflow();
  const { show, hide, withLoader } = useLoader();

  const contactPath = useAppointmentStepPath("contact-and-communication");
  const emergencyPath = useAppointmentStepPath("emergency-and-billing");

  React.useEffect(() => {
    // Hide the loader as soon as an error is reported — otherwise the
    // overlay covers the error Alert and the user sees an infinite spinner
    // (e.g. PAST_APPOINTMENT keeps `loading=false` but `demographics=null`).
    const hasError = !!(error || workflowError);
    const initialLoading = (workflowLoading || loading || !demographics) && !hasError;
    if (initialLoading) show("Loading checkin...");
    else hide();
    return () => hide();
  }, [workflowLoading, loading, demographics, error, workflowError, show, hide]);

  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [uploadedDL, setUploadedDL] = useState<string | null>(null);
  const [dlFile, setDlFile] = useState<File | null>(null);
  const dlInputRef = React.useRef<HTMLInputElement>(null);
  const inFlightSubmitRef = React.useRef(false);

  // Store initial state for change detection
  const [initialState, setInitialState] = useState<{
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    };
    driverLicense: {
      driverLicenseNumber: string;
      driverLicenseState: string;
      driverLicenseExpirationDate: string;
    } | null;
  } | null>(null);

  // Initialize state for change detection
  React.useEffect(() => {
    const address = demographics?.address;
    const dlInfo = demographics?.driverLicense;
    
    if (demographics) {
      setInitialState({
        address: {
          address1: address?.address1 || "",
          address2: address?.address2 || "",
          city: address?.city || "",
          state: address?.state || "",
          zip: address?.zip || "",
        },
        driverLicense: dlInfo ? {
          driverLicenseNumber: dlInfo.driverLicenseNumber || "",
          driverLicenseState: dlInfo.driverLicenseState || "",
          driverLicenseExpirationDate: dlInfo.driverLicenseExpirationDate || "",
        } : null,
      });
    }
  }, [demographics]);

  const handleDLUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDlFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setUploadedDL(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setUploadedDL(null);
      setDlFile(null);
    }
  };

  const handleRemoveDL = () => {
    setUploadedDL(null);
    setDlFile(null);
    if (dlInputRef.current) {
      dlInputRef.current.value = "";
    }
  };

  // Helper function to check if there are any changes
  const hasChanges = (formData: FormData): boolean => {
    if (!initialState) return true; // If no initial state, assume there are changes

    const currentAddress = {
      address1: (formData.get("address1") as string) || "",
      address2: (formData.get("address2") as string) || "",
      city: (formData.get("city") as string) || "",
      state: (formData.get("state") as string) || "",
      zip: (formData.get("zip") as string) || "",
    };

    // Check address changes
    if (
      currentAddress.address1 !== initialState.address.address1 ||
      currentAddress.address2 !== initialState.address.address2 ||
      currentAddress.city !== initialState.address.city ||
      currentAddress.state !== initialState.address.state ||
      currentAddress.zip !== initialState.address.zip
    ) {
      return true;
    }

    // Check driver license changes (optional block; compare if present)
    const currentDL = {
      driverLicenseNumber: (formData.get("dl_number") as string) || "",
      driverLicenseState: (formData.get("dl_state") as string) || "",
      driverLicenseExpirationDate: (formData.get("dl_expiration") as string) || "",
    };

    // If there's a new file uploaded, there are changes
    if (dlFile) {
      return true;
    }

    // Check if DL fields changed
    const initialDL = initialState.driverLicense;
    if (!initialDL) {
      // If initial state had no DL but now we're adding one, there are changes
      if (currentDL.driverLicenseNumber || currentDL.driverLicenseState || currentDL.driverLicenseExpirationDate) {
        return true;
      }
    } else {
      if (
        currentDL.driverLicenseNumber !== initialDL.driverLicenseNumber ||
        currentDL.driverLicenseState !== initialDL.driverLicenseState ||
        currentDL.driverLicenseExpirationDate !== initialDL.driverLicenseExpirationDate
      ) {
        return true;
      }
    }

    return false;
  };

  // True only when user is adding a new DL or updating existing DL (so we require number, state, photo).
  const hasDLAdditionOrUpdate = (formData: FormData): boolean => {
    const currentDL = {
      driverLicenseNumber: (formData.get("dl_number") as string)?.trim() || "",
      driverLicenseState: (formData.get("dl_state") as string)?.trim() || "",
      // driverLicenseExpirationDate: (formData.get("dl_expiration") as string)?.trim() || "",
    };
    console.log("currentDL", currentDL);
    const initialDL = initialState?.driverLicense ?? null;
    console.log("initialDL", initialDL);

    // Adding: no existing DL, user entered any of number, state, or file
    if (!initialDL) {
      console.log("currentDL.driverLicenseNumber", currentDL.driverLicenseNumber);
      return !!(currentDL.driverLicenseNumber || currentDL.driverLicenseState || (dlFile && dlFile.size > 0));
    }
    console.log("dlFile", dlFile);
    // Updating: existing DL and (fields changed or new file)
    if (dlFile && dlFile.size > 0) return true;
    console.log("currentDL.driverLicenseNumber !== initialDL.driverLicenseNumber", currentDL.driverLicenseNumber !== initialDL.driverLicenseNumber);
    console.log("currentDL.driverLicenseState !== initialDL.driverLicenseState", currentDL.driverLicenseState !== initialDL.driverLicenseState);
    return (
      currentDL.driverLicenseNumber !== initialDL.driverLicenseNumber ||
      currentDL.driverLicenseState !== initialDL.driverLicenseState
      // currentDL.driverLicenseExpirationDate !== initialDL.driverLicenseExpirationDate
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (inFlightSubmitRef.current) return;
    inFlightSubmitRef.current = true;
    try {

    const formData = new FormData(e.currentTarget);

    // If nothing changed (including existing DL untouched), just proceed
    if (!hasChanges(formData)) {
      navigate(emergencyPath);
      return;
    }

    const dlNumber = (formData.get("dl_number") as string)?.trim() || "";
    const dlState = (formData.get("dl_state") as string)?.trim() || "";
    const hasDlFile = !!(dlFile && dlFile.size > 0);

    // Require number, state, and photo only when adding or updating DL
    if (hasDLAdditionOrUpdate(formData)) {
      if (!dlNumber || !dlState || !hasDlFile) {
        setLocalError("To add a driver's license, please provide the license number, state, and a photo.");
        return;
      }
    }

    setSubmitting(true);
    setLocalError(null);
    try {
      await withLoader("Saving...", async () => {
        const payload: {
          address: {
            address1?: string;
            address2?: string;
            city?: string;
            state?: string;
            zip?: string;
            countryCode: string;
          };
          driverLicense?: { driverLicenseNumber: string; driverLicenseState: string };
        } = {
          address: {
            address1: (formData.get("address1") as string) || undefined,
            address2: (formData.get("address2") as string) || undefined,
            city: (formData.get("city") as string) || undefined,
            state: (formData.get("state") as string) || undefined,
            zip: (formData.get("zip") as string) || undefined,
            countryCode: "USA",
          },
        };

        if (hasDLAdditionOrUpdate(formData) && dlNumber && dlState && hasDlFile) {
          const patientId = demographics?.patientId;
          const departmentId = demographics?.departmentId;

          if (!patientId || !departmentId) {
            console.error("Missing patientId or departmentId for driver's license upload");
            const msg = "Missing patient information. Please try again.";
            setLocalError(msg);
            throw new Error(msg);
          }

          try {
            console.log("DL file info:", { name: dlFile!.name, type: dlFile!.type, size: dlFile!.size });

            const formDataUpload = new FormData();
            formDataUpload.append("image", dlFile!);

            await axiosInstance.put(
              `/api/athena/digital-checkin/patient/${encodeURIComponent(String(patientId))}/driverslicense?departmentId=${encodeURIComponent(String(departmentId))}`,
              formDataUpload,
              {
                headers: {
                  client: "axis",
                  domain: "axis",
                },
              },
            );
          } catch (uploadError: unknown) {
            console.error("Failed to upload DL image", uploadError);
            const errorMsg =
              (uploadError as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error ||
              (uploadError as { response?: { data?: { message?: string } } })?.response?.data?.message ||
              (uploadError as { message?: string })?.message ||
              "Failed to upload driver's license image";
            setLocalError(errorMsg);
            throw new Error(errorMsg);
          }

          payload.driverLicense = {
            driverLicenseNumber: dlNumber,
            driverLicenseState: dlState,
          };
        }

        try {
          await updateDemographics(payload);
        } catch {
          const msg = "Unable to save your information. Please try again.";
          setLocalError(msg);
          throw new Error(msg);
        }
        // Refetch workflow so the progress bar reflects backend truth
        // (step statuses, newly-completed steps) before we navigate.
        await refreshWorkflow();
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(emergencyPath);
          }
        }
      });
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : "Unable to save your information. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
    } finally {
      inFlightSubmitRef.current = false;
    }
  };

  const addressPath = useAppointmentStepPath("address-and-identity");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveAndExit = async () => {
    const formData = new FormData(document.forms[0] as HTMLFormElement);

    // Check if there are any changes before calling the API
    // For Save & Exit, we only check address changes (simplified)
    if (initialState) {
      const currentAddress = {
        address1: (document.querySelector('[name="address1"]') as HTMLInputElement)?.value || "",
        address2: (document.querySelector('[name="address2"]') as HTMLInputElement)?.value || "",
        city: (document.querySelector('[name="city"]') as HTMLInputElement)?.value || "",
        state: (document.querySelector('[name="state"]') as HTMLInputElement)?.value || "",
        zip: (document.querySelector('[name="zip"]') as HTMLInputElement)?.value || "",
      };

      const hasAddressChanges = 
        currentAddress.address1 !== initialState.address.address1 ||
        currentAddress.address2 !== initialState.address.address2 ||
        currentAddress.city !== initialState.address.city ||
        currentAddress.state !== initialState.address.state ||
        currentAddress.zip !== initialState.address.zip;

      if (!hasAddressChanges) {
        // No changes detected, navigate to next step in sequence or completion
        if (isAllStepsCompleted()) {
          navigate(`/appointments/${appointmentId}/complete`);
        } else {
          const nextStep = getNextStepInSequence(location.pathname);
          if (nextStep) {
            navigate(nextStep.stepUrl);
          } else {
            navigate(addressPath.replace("address-and-identity", "payments"));
          }
        }
        return;
      }
    }

    const payload = {
      address: {
        address1: (document.querySelector('[name="address1"]') as HTMLInputElement)?.value || undefined,
        address2: (document.querySelector('[name="address2"]') as HTMLInputElement)?.value || undefined,
        city: (document.querySelector('[name="city"]') as HTMLInputElement)?.value || undefined,
        state: (document.querySelector('[name="state"]') as HTMLInputElement)?.value || undefined,
        zip: (document.querySelector('[name="zip"]') as HTMLInputElement)?.value || undefined,
        countryCode: "USA",
      },
    };

    setSubmitting(true);
    setLocalError(null);
    try {
      await updateDemographics(payload);
      // Navigate to next step in sequence or completion
      if (isAllStepsCompleted()) {
        navigate(`/appointments/${appointmentId}/complete`);
      } else {
        const nextStep = getNextStepInSequence(location.pathname);
        if (nextStep) {
          navigate(nextStep.stepUrl);
        } else {
          navigate(addressPath.replace("address-and-identity", "payments"));
        }
      }
    } catch (err) {
      setLocalError("Unable to save your information. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const address = demographics?.address;
  const dlInfo = demographics?.driverLicense;

  return (
    <CheckinLayout
      title="Address & Identity"
      subtitle="Verify your address and identification. All fields marked with * are required."
    >
        {(error || localError) && (
          <div className="w-full max-w-[1200px] mb-2">
            <Alert
              type="error"
              onClose={() => {
                setLocalError(null);
              }}
            >
              {error || localError}
            </Alert>
          </div>
        )}
        <form
          className="bg-white rounded-xl border border-[#A4BDC0] shadow-md p-10 w-full max-w-[1200px] flex flex-col gap-8 mt-4"
          onSubmit={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Address Line 1"
              name="address1"
              placeholder="123 Main St"
              required
              defaultValue={address?.address1 || ""}
            />
            <FormInput
              label="Address Line 2"
              name="address2"
              placeholder="Apt, suite, floor, etc."
              defaultValue={address?.address2 || ""}
            />
            <FormInput
              label="City"
              name="city"
              placeholder="Enter your city"
              required
              defaultValue={address?.city || ""}
            />
            <FormSelect
              label="State"
              name="state"
              options={usStates}
              required
              defaultValue={address?.state || ""}
            />
            <FormInput
              label="Zip Code"
              name="zip"
              placeholder="5-digit zip"
              required
              defaultValue={address?.zip || ""}
            />
          </div>
          <div className="bg-[#F7F8FD] rounded-2xl border-2 border-[#A4BDC0] p-8 flex flex-col gap-7 mt-2">
              <div className="flex flex-col md:flex-row gap-6 w-full">
                <div className="flex-1">
                  <FormInput
                    label="Driver's License Number"
                    name="dl_number"
                    placeholder="Enter DL number"
                    defaultValue={dlInfo?.driverLicenseNumber || ""}
                  />
                </div>
                <div className="flex-1">
                  <FormSelect
                    label="DL State"
                    name="dl_state"
                    options={usStates}
                    defaultValue={dlInfo?.driverLicenseState || ""}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-[#1C1C1C] text-base mb-1">
                  Upload photo of Driver's License
                </label>
                <div className="relative w-full sm:w-[360px] h-[150px] flex flex-col items-center justify-center bg-white border-2 border-dashed border-[#A4BDC0] rounded-xl hover:border-[#01545D] transition-colors duration-200 cursor-pointer group p-2">
                  <input
                    id="dl_upload"
                    name="dl_upload"
                    type="file"
                    accept="image/*"
                    onChange={handleDLUpload}
                    ref={dlInputRef}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  {!uploadedDL && (
                    <div className="flex flex-col items-center justify-center pointer-events-none z-0">
                      <svg
                        className="w-8 h-8 text-[#A4BDC0] mb-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <span className="text-[#818BA0] font-medium text-sm">
                        Drop or click to upload
                      </span>
                    </div>
                  )}
                  {uploadedDL && (
                    <div className="flex flex-col items-center justify-center z-0 w-full h-full">
                      <span className="text-xs text-[#5D6A85] mb-1">Preview</span>
                      <img
                        src={uploadedDL}
                        alt="Driver's license preview"
                        className="object-contain max-h-full max-w-full rounded border border-[#BEC5D2]"
                      />
                    </div>
                  )}
                </div>
                {uploadedDL && (
                  <button
                    type="button"
                    onClick={handleRemoveDL}
                    className="mt-1 text-[#01545D] text-sm underline hover:text-[#0b7981] inline-block self-start"
                  >
                    Remove image
                  </button>
                )}
              </div>
            </div>
          <div className="flex gap-5 w-full mt-8">
            <AppButton
              variant="secondary"
              type="button"
              onClick={() => {
                const previousStep = getPreviousStep(location.pathname);
                if (previousStep) {
                  navigate(previousStep.stepUrl);
                } else {
                  navigate(contactPath);
                }
              }}
              disabled={!!workflowError || submitting || loading}
              className="flex-1"
            >
              Back
            </AppButton>
            {/* <AppButton
              variant="secondary"
              type="button"
              onClick={handleSaveAndExit}
              disabled={submitting || loading}
              className="flex-1"
            >
              {submitting ? "Saving..." : "Save & Exit"}
            </AppButton> */}
            <AppButton type="submit" disabled={!!workflowError || submitting || loading} className="flex-1">
              {submitting ? "Saving..." : "Continue"}
            </AppButton>
          </div>
        </form>
    </CheckinLayout>
  );
};

export default AddressAndIdentity;
